import { resolveAppStatsFeature } from '~/constants/appStatsFeatures'
import { scrubPotentialPii } from '~/utils/appStatsPiiScrub'
import { useApiMonOrga } from '~/composables/api/useApiMonOrga'

const MAX_REPORTS_PER_MINUTE = 8
const sentTimestamps: number[] = []

function messageFromRejection(reason: unknown): string {
  if (reason instanceof Error) return reason.message
  if (typeof reason === 'string') return reason
  return 'unhandledrejection'
}

function canSendNow(): boolean {
  const now = Date.now()
  while (sentTimestamps.length > 0 && now - sentTimestamps[0]! > 60_000) {
    sentTimestamps.shift()
  }
  if (sentTimestamps.length >= MAX_REPORTS_PER_MINUTE) {
    return false
  }
  sentTimestamps.push(now)
  return true
}

/**
 * Mini reporter d’erreurs JS front (défense en profondeur + anti-flood).
 */
export default defineNuxtPlugin((nuxtApp) => {
  const { loggedIn } = useOidcAuth()
  const router = useRouter()

  async function reportError(rawMessage: string, errorName: string): Promise<void> {
    if (!loggedIn.value || !canSendNow()) {
      return
    }
    const routeName = router.currentRoute.value.name?.toString() ?? null
    try {
      await useApiMonOrga('POST', '/v1/stats/error', undefined, {
        source: 'frontend',
        routeName: routeName ? scrubPotentialPii(routeName, 160) : null,
        feature: resolveAppStatsFeature(routeName),
        message: scrubPotentialPii(rawMessage, 500),
        context: {
          component: 'frontend',
          errorName: scrubPotentialPii(errorName, 120),
        },
      })
    } catch {
      // jamais bloquant
    }
  }

  nuxtApp.vueApp.config.errorHandler = (error, _instance, info) => {
    const message = error instanceof Error ? error.message : String(error)
    void reportError(`${message} (${info})`, error instanceof Error ? error.name : 'VueError')
  }

  if (import.meta.client) {
    window.addEventListener('error', (event) => {
      void reportError(event.message || 'window.onerror', event.error?.name ?? 'WindowError')
    })
    window.addEventListener('unhandledrejection', (event) => {
      const reason = event.reason
      const message = messageFromRejection(reason)
      void reportError(message, reason instanceof Error ? reason.name : 'UnhandledRejection')
    })
  }
})
